import {
  createContext,
  useCallback,
  useState,
  useContext,
  // useEffect,\
} from 'react';
import { toast } from 'react-toastify';
import { IUser } from '../@types/system';
import api, { clearSession } from '../service/api';
import { useLoading } from '../store/useLoading';
import { useStore } from '../store';

interface AuthState {
  token: string;
  user: IUser;
}

interface SignInCredentials {
  email: string;
  password: string;
}

interface AuthContext {
  user: IUser;
  signIn(credentials: SignInCredentials): Promise<void>;
  signOut(): Promise<void>;
}

const AuthContext = createContext<AuthContext>({} as AuthContext);

const AuthProvider = ({ children }: any) => {
  const setLoading = useLoading((state) => state.setLoading);
  const setUser = useStore((state) => state.setUser);
  const setToken = useStore((state) => state.setToken);
  const clearLogin = useStore((state) => state.clearLogin);

  const [data, setData] = useState<AuthState>(() => {
    const tokenLoc = localStorage.getItem('@FantasyADM:token');
    const userLoc = localStorage.getItem('@FantasyADM:user');

    if (tokenLoc && userLoc) {
      api.defaults.headers.common['Authorization'] = `Bearer ${tokenLoc}`;
      setToken(tokenLoc);
      setUser(JSON.parse(userLoc));

      return { token: tokenLoc, user: JSON.parse(userLoc) };
    }

    return {} as AuthState;
  });

  const signIn = useCallback(async ({ email, password }: SignInCredentials) => {
    setLoading(true);

    if (email === '' || password === '') {
      toast.error('Todos os campos são obrigatorios', {
        containerId: 'toast',
      });
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/sessions/admin', {
        email,
        password,
      });

      const { token, user, refresh_token } = response.data;

      if (user.user_type === 'USER') {
        toast.error('Este usuário não tem permissões de administrador', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'colored',
        });
        setLoading(false);
        return;
      }

      setUser(user);
      setToken(token);
      localStorage.setItem('@FantasyADM:token', token);
      localStorage.setItem('@FantasyADM:user', JSON.stringify(user));
      localStorage.setItem('@FantasyADM:refreshToken', refresh_token);

      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      setData({ token, user });
      setLoading(false);
    } catch (e: any) {
      setLoading(false);

      // console.log(e.response.status);

      if (e.response.status === 400) {
        toast.error('E-mail ou senha não conferem', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'colored',
        });
        return;
      }
    }
  }, []);

  const signOut = useCallback(async () => {
    const refreshToken = localStorage.getItem('@FantasyADM:refreshToken');

    if (refreshToken) {
      try {
        await api.post('/sessions/logout', { refresh_token: refreshToken });
      } catch {
        // não bloquear o logout local por falha de rede
      }
    }

    clearSession();

    clearLogin();

    setData({} as AuthState);
  }, []);

  return <AuthContext.Provider value={{ user: data.user, signIn, signOut }}>{children}</AuthContext.Provider>;
};

function useAuth(): AuthContext {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

// eslint-disable-next-line react-refresh/only-export-components
export { AuthProvider, useAuth };
